import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * A single LLM task-route row, as returned by the `llm-task-routes` find.
 *
 * The API joins each `llm_task_routes` row to its task definition (from the
 * `LLM_TASKS` registry) and to the assigned provider, so the find result is a
 * fully-denormalised view: the task's static metadata (label/description/
 * suggested), the chosen provider (id + name + type), the optional model
 * override, and the optional fallback provider. A task with `providerId === null`
 * is "unrouted" and falls through to the app default at call time.
 */
export interface LlmTaskRoute {
  taskKey: string;
  label: string;
  description: string;
  /** UI grouping (e.g. "Meeting analysis"); from the server task registry. */
  category?: string | null;
  /** Stable ordering within the registry (lower first). */
  sortOrder?: number | null;
  suggested?: "local" | "cloud" | null;
  /** The prompt carries a client database (may hold patient data): a private Ollama only; the API refuses anything else. */
  localOnly?: boolean;
  providerId: string | null;
  providerName: string | null;
  providerType: string | null;
  model: string | null;
  fallbackProviderId: string | null;
  fallbackProviderName: string | null;
}

/**
 * Provider option for the routing dropdowns. Loaded from `llm-providers` (the
 * masked find — secrets are never returned), trimmed to the fields the routing
 * selects need: an id/name for the option, a type for any provider-kind hint,
 * and the provider's `defaultModel` so the Model field can show it as a
 * placeholder when the route leaves the model blank.
 */
export interface LlmRoutingProvider {
  id: string;
  name: string;
  type: string;
  defaultModel: string | null;
}

/** The editable per-row payload patched to `llm-task-routes/<taskKey>`. */
export interface LlmTaskRoutePatch {
  providerId: string | null;
  model: string | null;
  fallbackProviderId: string | null;
}

/**
 * View store for the AI → Task Routing admin page.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The LLM task routes and provider options are read through
 * the registered `llmTaskRoutesStore` / `llmProvidersStore` ServiceStores (see
 * serviceStores/ai.stores.ts): `find()` populates each store's entity cache,
 * and {@link tasks} / {@link providers} are computed views over those caches
 * (per the MobX rule, API entities live in the ServiceStore, never in a
 * ViewStore).
 *
 * Each task row is edited in place: the table binds Provider/Model/Fallback
 * controls to the {@link tasks} row via {@link setTaskField}, and
 * {@link saveTask} persists that row with a per-row PATCH keyed by `taskKey`.
 * Because the loaded rows live in the (shared) entity cache, in-flight edits are
 * held in a local {@link drafts} map keyed by `taskKey` and merged onto the
 * cached row by {@link tasks} — the cache itself is only mutated by the save's
 * reload. No secrets are involved here — routing only references providers by id.
 */
class LlmRoutingStore {
  rootStore: RootStore;

  /**
   * Pending per-row field edits, keyed by `taskKey`. Holds only the fields the
   * admin has changed (a partial patch); {@link tasks} merges these onto the
   * ServiceStore's cached rows so the bound controls stay controlled without
   * mutating the shared entity cache. Cleared per-key after a successful save.
   */
  private drafts = new Map<string, Partial<LlmTaskRoute>>();

  loading = false;
  error: string | null = null;

  // Per-row save state. Only one row saves at a time; `savingTaskKey` is the
  // task currently being persisted (drives the row's spinner/disabled button).
  savingTaskKey: string | null = null;
  saveError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `llm-task-routes` ServiceStore (see serviceStores/ai.stores.ts). */
  private get taskRoutesService(): ServiceStoreType | null {
    return (this.rootStore.llmTaskRoutesStore as ServiceStoreType | null) ?? null;
  }

  /** The registered `llm-providers` ServiceStore (masked find — secrets are never returned). */
  private get providersService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("llm-providers") ?? this.rootStore.llmProvidersStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * Task-route rows. Per the MobX rule the loaded rows live in the ServiceStore
   * cache (populated by `find`); this is a computed view that overlays any
   * pending per-row {@link drafts} so the bound editing controls stay controlled
   * without mutating the shared cache.
   */
  get tasks(): LlmTaskRoute[] {
    const rows = (this.taskRoutesService?.entities ?? []) as unknown as LlmTaskRoute[];
    if (this.drafts.size === 0) return rows;
    return rows.map((row) => {
      const draft = this.drafts.get(row.taskKey);
      return draft ? { ...row, ...draft } : row;
    });
  }

  /**
   * Provider options for the routing dropdowns. Computed from the `llm-providers`
   * ServiceStore cache, trimmed to the fields the selects need.
   */
  get providers(): LlmRoutingProvider[] {
    const rows = (this.providersService?.entities ?? []) as unknown as Array<{
      id: string;
      name: string;
      type: string;
      defaultModel?: string | null;
    }>;
    return rows.map((p) => ({
      id: p.id,
      name: p.name,
      type: p.type,
      defaultModel: p.defaultModel ?? null,
    }));
  }

  // ---- Derived data -------------------------------------------------------

  /**
   * Tasks grouped by their registry category, in the order the server returns
   * them (already sorted by sortOrder). Each group carries its own unrouted
   * count so the UI can badge "N unrouted" per section.
   */
  get groupedTasks(): Array<{ category: string; tasks: LlmTaskRoute[]; unroutedCount: number }> {
    const groups: Array<{ category: string; tasks: LlmTaskRoute[]; unroutedCount: number }> = [];
    const byCategory = new Map<string, { category: string; tasks: LlmTaskRoute[]; unroutedCount: number }>();
    for (const task of this.tasks) {
      const category = task.category || "Other";
      let group = byCategory.get(category);
      if (!group) {
        group = { category, tasks: [], unroutedCount: 0 };
        byCategory.set(category, group);
        groups.push(group);
      }
      group.tasks.push(task);
      if (!task.providerId) group.unroutedCount += 1;
    }
    return groups;
  }

  /** Total tasks with no provider routed (fall through to the app default). */
  get unroutedCount(): number {
    return this.tasks.reduce((n, t) => n + (t.providerId ? 0 : 1), 0);
  }

  taskByKey(taskKey?: string | null): LlmTaskRoute | null {
    if (!taskKey) return null;
    return this.tasks.find((t) => t.taskKey === taskKey) || null;
  }

  providerById(id?: string | null): LlmRoutingProvider | null {
    if (!id) return null;
    return this.providers.find((p) => p.id === id) || null;
  }

  /** The chosen provider's default model — used as the Model field placeholder. */
  defaultModelFor(providerId?: string | null): string {
    return this.providerById(providerId)?.defaultModel || "";
  }

  get isSaving(): boolean {
    return this.savingTaskKey !== null;
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    this.loading = true;
    this.error = null;
    try {
      // Each ServiceStore.find populates its own entity cache, which `tasks` and
      // `providers` read. Pending edits are discarded once fresh rows arrive.
      //
      // findOrThrow for BOTH: a bare find swallows a failure (resolves null),
      // which left this catch dead and the page reading "No AI tasks are
      // available to route yet" — or, if only providers failed, offering
      // provider dropdowns with nothing in them as though none existed.
      await Promise.all([
        findOrThrow(this.taskRoutesService, { query: {} }),
        findOrThrow(this.providersService, { query: { $limit: 200 } }),
      ]);
      runInAction(() => {
        this.drafts.clear();
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load task routing");
      });

      console.error("Failed to load LLM task routing:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.load();
  }

  // ---- Per-row editing ----------------------------------------------------

  /**
   * Mutate one field of a task row in place so the bound controls stay
   * controlled. Selecting "— Use default —" passes an empty string, which we
   * normalise to `null` (the unrouted / no-fallback state). Choosing a new
   * provider clears any stale model override so the new provider's default
   * placeholder shows through until the admin types an override.
   */
  setTaskField(
    taskKey: string,
    field: keyof LlmTaskRoutePatch,
    value: string | null
  ) {
    const task = this.taskByKey(taskKey);
    if (!task) return;
    // Clear the row-level save error as soon as the admin edits anything.
    this.saveError = null;

    const normalized = value === "" ? null : value;
    // Stage the edit in the per-row draft (merged onto the cached row by
    // `tasks`) instead of mutating the shared ServiceStore entity.
    const draft: Partial<LlmTaskRoute> = { ...this.drafts.get(taskKey) };

    if (field === "providerId") {
      draft.providerId = normalized;
      // Switching providers invalidates a previously-typed model override.
      draft.model = null;
    } else if (field === "fallbackProviderId") {
      draft.fallbackProviderId = normalized;
    } else {
      draft.model = normalized;
    }

    this.drafts.set(taskKey, draft);
  }

  // ---- Saving -------------------------------------------------------------

  /**
   * Persist one task's routing. The ServiceStore PATCHes
   * `llm-task-routes/<taskKey>` (the taskKey is the route's stable,
   * human-readable id) with the chosen provider, optional model override, and
   * optional fallback. On success the server echoes the freshly-denormalised
   * row; we overlay that (or a locally-derived equivalent) onto the row via the
   * per-row draft so provider names/types stay in sync without mutating the
   * shared entity cache directly.
   */
  async saveTask(
    taskKey: string,
    patch?: Partial<LlmTaskRoutePatch>
  ): Promise<boolean> {
    const current = this.taskByKey(taskKey);
    if (!current) return false;

    this.savingTaskKey = taskKey;
    this.saveError = null;
    try {
      const payload: LlmTaskRoutePatch = {
        providerId:
          patch && "providerId" in patch
            ? patch.providerId ?? null
            : current.providerId,
        model:
          patch && "model" in patch ? patch.model ?? null : current.model,
        fallbackProviderId:
          patch && "fallbackProviderId" in patch
            ? patch.fallbackProviderId ?? null
            : current.fallbackProviderId,
      };

      const updated = (await this.taskRoutesService?.patch(
        taskKey,
        payload as never
      )) as unknown as LlmTaskRoute | null | undefined;

      runInAction(() => {
        // Prefer the server's denormalised row (it carries the resolved
        // provider/fallback names). Fall back to merging the payload onto the
        // existing row if the API returns nothing useful. The result is stored
        // as the row's draft overlay — the cached entity stays untouched until
        // the next reload.
        if (updated && typeof updated === "object" && updated.taskKey) {
          this.drafts.set(taskKey, { ...updated });
        } else {
          this.drafts.set(taskKey, {
            ...payload,
            providerName: this.providerById(payload.providerId)?.name ?? null,
            providerType: this.providerById(payload.providerId)?.type ?? null,
            fallbackProviderName:
              this.providerById(payload.fallbackProviderId)?.name ?? null,
          });
        }
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.saveError =
            err instanceof Error ? err.message : "Failed to save task routing";
        }
      });

      console.error("saveTask failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.savingTaskKey = null;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.drafts = new Map<string, Partial<LlmTaskRoute>>();
    this.loading = false;
    this.error = null;
    this.savingTaskKey = null;
    this.saveError = null;
  }
}

export default LlmRoutingStore;
